import React from 'react'
import { View, TouchableOpacity, StyleSheet, Animated, FlatList } from 'react-native'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { useTheme } from '@react-navigation/native'
import { Text, Icon } from '../atoms'
import { Theme } from '@theme'
import { Moment, useGetMoments } from '@hooks'
import { useLanguage } from '@context'
import { getMomentLabel } from '@utils'

interface MomentsListProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  moments: Moment[]
  height?: number
  onToggle: (moment: Moment) => void
}

const AnimatedIcon = ({ isSelected }: { isSelected: boolean }) => {
  const scaleValue = React.useRef(new Animated.Value(1)).current

  React.useEffect(() => {
    let scaleAnimation: Animated.CompositeAnimation | null = null

    scaleAnimation = Animated.sequence([
      Animated.timing(scaleValue, {
        toValue: 0.8,
        duration: 100,
        useNativeDriver: true,
      }),
      Animated.timing(scaleValue, {
        toValue: 1,
        duration: 100,
        useNativeDriver: true,
      }),
    ])
    scaleAnimation.start()

    // Cleanup function pour arrêter l'animation si le composant se démonte
    return () => {
      scaleAnimation?.stop()
    }
  }, [isSelected, scaleValue])

  // Cleanup général au démontage du composant
  React.useEffect(() => {
    return () => {
      // Arrêter toutes les animations en cours
      scaleValue.stopAnimation()
    }
  }, [scaleValue])

  return (
    <Animated.View style={{ transform: [{ scale: scaleValue }] }}>
      <Icon
        name={isSelected ? 'blueRadio' : 'redRadio'}
        size={35}
      />
    </Animated.View>
  )
}

export const MomentsList = <TFieldValues extends FieldValues>({
  control,
  name,
  moments,
  onToggle,
  height,
}: MomentsListProps<TFieldValues>) => {
  const theme = useTheme()
  const { t, currentLanguage } = useLanguage()
  const styles = getStyle(theme)

  const { data: allMoments } = useGetMoments()
  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState: { error } }) => (
        <>
          <View style={{ height: height ? height : undefined, flex: height ? undefined : 1, width: '100%' }}>
            <FlatList
              data={allMoments}
              keyExtractor={item => item.id}
              style={{ flex: 1 }}
              showsVerticalScrollIndicator={false}
              bounces={false}
              renderItem={({ item }) => {
                const isSelected = !!moments.find(m => m.id === item.id)
                return (
                  <TouchableOpacity
                    key={item.id}
                    style={styles.preferenceItem}
                    onPress={() => onToggle(item)}
                    activeOpacity={0.7}
                  >
                    <View style={styles.leftSection}>
                      <Icon
                        name={item.code}
                        size={24}
                        color={isSelected ? theme.colors.black : theme.colors.primary}
                      />
                      <Text
                        style={styles.preferenceText}
                        type="body"
                        color={isSelected ? theme.colors.black : theme.colors.primary}
                      >
                        {getMomentLabel(t, item.code, item.nameFr, item.nameEn, currentLanguage, item.nameEs)}
                      </Text>
                    </View>
                    <AnimatedIcon isSelected={isSelected} />
                  </TouchableOpacity>
                )
              }}
            />
          </View>
          
          {error && (
            <Text style={styles.errorMessage} type="microText" color={theme.colors.error}>
              {error.message}
            </Text>
          )}
        </>
      )}
    />
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      backgroundColor: theme.colors.background,
      borderRadius: theme.border.large,
      paddingVertical: theme.spacing.sm,
    },
    scrollView: {
      flex: 1,
    },
    preferenceItem: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.grey,
    },
    leftSection: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
    },
    preferenceText: {
      marginLeft: theme.spacing.lg,
      fontSize: 16,
    },
    errorMessage: {
      alignSelf: 'center',
      paddingVertical: theme.spacing.sm,
      color: theme.colors.error,
    },
  })